import { useState, useEffect } from 'react';
import { fetchParsedResumeText, handleResumeUpload } from './handleResume'; // Importing functions from your provided file
import { httpsCallable } from 'firebase/functions';
import { functions, auth, firestore, storage } from '../firebase'; // Import your initialized Firebase functions
import './ResumeBuilder.css';
import { collection, getDocs } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import PaymentModal from './PaymentModal';
import Handlebars from 'handlebars';
import "react-quill/dist/quill.snow.css";
import ResumeEditor from './ResumeEditor2';
import { fetchResumeUploadedStatus } from './fetchResumeUploadedStatus';
import GuestAuth from './GuestAuth';
import { doc, setDoc } from "firebase/firestore";
import { parseFile } from './ParseFile'; // Assuming you have a function to parse the resume file

const ResumeBuilderTest = () => {
  const [userId, setUserId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [currentResume, setCurrentResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [previewResume, setPreviewResume] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [step, setStep] = useState(1); // Track which step the user is on
  const [resumeData, setResumeData] = useState({});
  const [resumeUploaded, setResumeUploaded] = useState(false);


useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      setUserId(user.uid);
      
      // Fetch whether the user has uploaded a resume
      const hasResume = await fetchResumeUploadedStatus(user.uid);
      setResumeUploaded(hasResume);
    } else {
      setUserId(null);
      setResumeUploaded(false);
    }
  });

  return () => unsubscribe();
}, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templatesCollection = collection(firestore, '/templates');
        const templatesSnapshot = await getDocs(templatesCollection);
    
        const fetchedTemplates = await Promise.all(
          templatesSnapshot.docs.map(async (doc) => {
            const templateData = doc.data();
            const storageRef = ref(storage, `/template_previews/${doc.id}.png`); // Ensure extension is correct
            try {
              const previewUrl = await getDownloadURL(storageRef);
              return { id: doc.id, ...templateData, preview_url: previewUrl };
            } catch (error) {
              console.error("Failed to fetch preview for:", doc.id, error);
              return { id: doc.id, ...templateData, preview_url: null };
            }
          })
        );
    
        setTemplates(fetchedTemplates);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };
  
    fetchTemplates();
  }, []);
  
  const handleResumeSelection = async (useCurrentResume) => {
    try {
      setLoading(true);
      setProgress('Fetching resume...');
      if (useCurrentResume) {
        const resume = await fetchParsedResumeText(userId);
        if (!resume) throw new Error('No current resume found.');
        setCurrentResume(resume);
      } else {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.pdf,.docx';
        fileInput.onchange = async (event) => {
          const file = event.target.files[0];
          if (file) {
            // Step 1: Upload the resume and get its URL.
            setLoading(true);
            setProgress('Uploading resume...');
            const resumeUrl = await handleResumeUpload(file, userId);
            
            // Step 2: Invoke the resume parsing process using the returned URL.
            setProgress('Parsing resume...');
            const parsedData = await parseFile(resumeUrl);
            
            if (!parsedData) {
              throw new Error('Failed to parse the resume.');
            }
            
            // Store the parsed resume in Firestore under the correct path.
            const userDocRef = doc(firestore, "uploads", userId, "parsedDocuments", "parsedResume");
            await setDoc(userDocRef, {
              type: "resume",
              content: parsedData,
              timestamp: new Date(),
            });
            
            // Update the currentResume state with the parsed text.
            setCurrentResume(parsedData);
          }
        };
        fileInput.click();
      }
    } catch (error) {
      console.error('Error selecting resume:', error);
      alert(error.message);
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  const enhanceResume = async (resumeText) => {
    const callableFunction = httpsCallable(functions, 'enhanceResume');
    try {
      const result = await callableFunction({ resume: resumeText });
      return result.data; // Expected to be in a universal format
    } catch (error) {
      console.error('Error enhancing resume:', error);
      throw error;
    }
  };

  const applyTemplate = async (templateId, enhancedResumeResponse) => {
    try {
        if (!enhancedResumeResponse) {
            throw new Error("Unexpected error occurred, please try again later.");
        }

        // Fetch the selected template's HTML from Firebase Storage
        const templateRef = ref(storage, `/templates/${templateId}.html`);
        const templateUrl = await getDownloadURL(templateRef);
        const response = await fetch(templateUrl);
        const templateHtml = await response.text();

        // Compile the Handlebars template
        const template = Handlebars.compile(templateHtml);

        // ✅ Use the full response object without destructuring
        const populatedHtml = template({ resumeData: enhancedResumeResponse });

        return populatedHtml;
    } catch (error) {
        console.error("Error applying template:", error);
        throw error;
    }
};


const handleEnhanceAndFormat = async () => {
  if (!currentResume || !selectedTemplate) {
      alert("Please select a template and a resume before proceeding.");
      return;
  }

  setLoading(true);
  setProgress("Enhancing resume...");

  try {
      const enhancedResponse = await enhanceResume(currentResume);

      if (enhancedResponse.status === "LIMIT_REACHED") {
          setShowPopup(true);
          setLoading(false);
          return;
      }

      setProgress("Formatting resume...");

      // Ensure enhanced resume object is extracted correctly
      const { enhancedResume: resumeData } = enhancedResponse;
      setResumeData(enhancedResponse.enhancedResume);  // Store extracted resume data

      // Apply the selected template to format the resume
      const formattedResume = await applyTemplate(selectedTemplate, resumeData);

      // ✅ Ensure this updates the preview correctly
      setPreviewResume(formattedResume);
      setStep(4); // Move to preview step

  } catch (error) {
      console.error("Error enhancing or formatting resume:", error);
      alert("Failed to enhance or format resume.");
  } finally {
      setLoading(false);
  }
};

    // Automatically move to Step 2 when a template is selected
    useEffect(() => {
        if (selectedTemplate) setStep(2);
    }, [selectedTemplate]);

    // Automatically move to Step 3 when a resume is selected
    useEffect(() => {
        if (currentResume) {
            setStep(3);
            handleEnhanceAndFormat(); // Auto-enhance after resume selection
        }
    }, [currentResume]);

    // Automatically move to Step 4 when the preview is ready
    useEffect(() => {
        if (previewResume) setStep(4);
    }, [previewResume]);

    return (
        <div id="resume-builder">
            <h1>Resume Builder</h1>
            {loading ? (
                <div className="spinner-container">
                    <div className="spinner"></div>
                    <p>{progress}</p>
                </div>
            ) : (
                <>
                    {/* Step 1: Select a Template */}
                    {step === 1 && (
                        <div className="section">
                            <h2>Select a Template</h2>
                            <div className="template-grid">
                                {templates.map((template) => (
                                    <div
                                        key={template.id}
                                        className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedTemplate(template.id)}
                                    >
                                        <img src={template.preview_url} alt={template.name} />
                                        <p>{template.name}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 2: Choose Resume */}
                    {step === 2 && (
                        <div className="section">
                            <h2>Choose Your Resume</h2>
                            <div className="button-group">
                            {userId && resumeUploaded && (
                              <button onClick={() => handleResumeSelection(true)}>
                                Use Current Resume
                              </button>
                            )}
                            <button onClick={() => handleResumeSelection(false)}>
                              Upload Resume
                            </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Enhancing Resume (No user action needed) */}
                    {step === 3 && (
                        <div className="section">
                            <h2>Enhancing Resume...</h2>
                            <p>Please wait while we format your resume.</p>
                        </div>
                    )}

                    {/* Step 4: Preview & Download */}
                    {step === 4 && previewResume && (
                      <ResumeEditor
                          initialResume={previewResume}
                          resumeData={resumeData}
                          templateId={selectedTemplate}
                      />
                  )}

                </>
            )}

            {/* Render Payment Modal */}
            <PaymentModal isOpen={showPopup} onClose={() => setShowPopup(false)} />
        </div>
    );
};

export default function WrappedResumeBuilder() {
  // Wrap ResumeBuilder with GuestAuth so that a guest is signed in if no user exists.
  return (
    <GuestAuth>
      <ResumeBuilderTest />
    </GuestAuth>
  );
}
